import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { faFile } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const ContentList = ({ content, url, title = null, small = false, anchor, loading, active }) => {
    const [isOpen, setIsOpen] = useState(false);
    const contentRef = useRef(null);

    useEffect(() => {
        if (!loading) {
            setTimeout(() => {
                setIsOpen(active);

                const element = document.getElementById(anchor);
                if (element) {
                    element.scrollIntoView();
                }
            }, 250);
        }
    }, [active, loading, anchor]);

    return (
        <div className="my-10" id={anchor}>
            {title && (
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`${small ? 'mb-4' : 'mb-8'} w-full flex justify-between items-center gap-4 md:gap-10 whitespace-nowrap cursor-pointer`}
                >
                    <TitleText title={title} small={small} />

                    <div className="w-full border border-white opacity-20" />

                    <span
                        className={`relative block min-w-8 sm:min-w-10 size-8 sm:size-10 bg-secondary rounded-full opacity-80 transition-all hover:opacity-100 after:content-[''] after:absolute after:left-2.5 sm:after:left-3 after:size-3 sm:after:size-4 after:border-b-2 after:border-l-2 after:border-white ${isOpen ? 'after:top-3 sm:after:top-4 after:rotate-[135deg]' : 'after:top-2 after:-rotate-45'}`}
                    ></span>
                </button>
            )}

            {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 xl:gap-10 opacity-20">
                    {[...Array(4)].map((_, index) => (
                        <div
                            key={index}
                            role="status"
                            className="flex items-center justify-center h-0 pb-[90%] md:pb-[103%] max-w-sm bg-gray-300 rounded-lg animate-pulse dark:bg-gray-700"
                        >
                            <span className="sr-only">Loading...</span>
                        </div>
                    ))}
                </div>
            ) : (
                <div
                    ref={contentRef}
                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 transition-all duration-500 ease-in-out overflow-hidden"
                    style={{
                        maxHeight: isOpen ? `${contentRef.current?.scrollHeight}px` : '0px',
                    }}
                >
                    {content.map((slide, index) => (
                        <SlideItem key={index} slide={slide} url={url} small={small} />
                    ))}
                </div>
            )}
        </div>
    );
};

const TitleText = ({ title, small }) => {
    const [main, ...rest] = title.split(' - ');
    const sub = rest.join(' - ').trim();

    return (
        <h3 className="flex flex-col text-left text-secondary font-bold">
            <span className={`text-xl ${small ? 'lg:text-2xl' : 'lg:text-4xl'}`}>{main.trim()}</span>
            {sub && <span className={`text-white text-base ${small ? 'lg:text-lg' : 'lg:text-2xl'} font-semibold opacity-75`}>{sub}</span>}
        </h3>
    );
};

const SlideItem =({ slide, url, anchor, small }) => (
    <Link
        to={`${
            slide.produto_slug
                ? `${url.split('//')[0]}/${slide.produto_slug}/${url.split('//')[1]}${slide.id}`
                : `${url + (slide.tipo_slug ? slide.tipo_slug + '/' : '') + (slide.slug ? slide.slug : slide.id)}`
        }`}
        className="group block relative overflow-hidden rounded-xl border border-neutral-500"
    >
        {slide.preview ? (
            <img src={slide.preview} className="w-full" loading="lazy" alt={slide.nome || 'Slide preview'} />
        ) : (
            <div className="relative w-full h-0 pb-[calc(100%_*_43_/_32)] bg-white">
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <span className="fa-layers fa-fw text-6xl md:text-8xl lg:text-9xl">
                        <FontAwesomeIcon icon={faFile} className="text-secondary" />
                        <span className="fa-layers-text fa-inverse text-sm md:text-2xl font-black mt-2 uppercase">{slide.extensao}</span>
                    </span>
                </div>
            </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent to-40% transition-all origin-bottom group-hover:scale-y-125" />

        <h5
            className={`absolute left-4 bottom-16 max-sm:text-sm max-sm:leading-tight max-xl:text-base right-4 ${small ? 'xl:bottom-16 text-lg' : 'xl:bottom-20 text-xl'} text-white font-semibold max-xl:leading-tight leading-tight translate-y-full`}
        >
            {slide.nome}
        </h5>

        {slide.lancamento && (
            <span className="absolute top-2 left-2 flex items-center gap-1 sm:gap-2 bg-tertiary px-2 py-1 rounded-md text-white text-xs font-semibold max-sm:tracking-tight xl:tracking-wide 2xl:tracking-widest uppercase max-sm:scale-90 origin-top-left">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14.998"
                    height="15"
                    viewBox="0 0 14.998 15"
                    className="fill-secondary max-sm:-mt-0.5"
                >
                    <path
                        d="M16.458,6.883a.751.751,0,0,0-.651-.507l-4.275-.34-1.85-4.1a.748.748,0,0,0-1.366,0l-1.85,4.1-4.275.34a.75.75,0,0,0-.464,1.285l3.159,3.08L3.768,15.579a.75.75,0,0,0,1.147.793L9,13.65l4.083,2.722a.751.751,0,0,0,1.138-.829l-1.372-4.8,3.4-3.061a.751.751,0,0,0,.208-.8Z"
                        transform="translate(-1.5 -1.498)"
                    />
                </svg>
                Lançamento
            </span>
        )}
    </Link>
);

export default ContentList;
